import { useTranslation } from 'react-i18next';
import { SkeletonCard } from '$lib/components/ui/Skeleton';
import { PortalCard } from './PortalCard';
import type { PipelineHealthPortal, ScrapeRunDetail } from '$lib/api/adminScraper';

export interface PortalCardsRowProps {
  portals: PipelineHealthPortal[];
  // Map of `portal_slug → active run detail`. Only populated for portals
  // currently running. Cards without an entry render the static metadata
  // pulled from /pipeline/health.
  activeRunsByPortal: Record<string, ScrapeRunDetail | null>;
  loading: boolean;
  onStartFreshRun: (portalSlug: string) => void;
  onRetry: (portalSlug: string) => void;
}

export function PortalCardsRow({
  portals,
  activeRunsByPortal,
  loading,
  onStartFreshRun,
  onRetry
}: PortalCardsRowProps) {
  const { t } = useTranslation();

  if (loading && portals.length === 0) {
    // Four skeletons matches the seeded portal count and keeps the layout
    // stable when the live data lands.
    return (
      <section
        aria-label={t('admin.dashboard.portalCardsAriaLabel')}
        className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        {Array.from({ length: 4 }, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </section>
    );
  }

  return (
    <section
      data-testid="portal-cards"
      aria-label={t('admin.dashboard.portalCardsAriaLabel')}
      className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      {portals.map((p) => (
        <PortalCard
          key={p.slug}
          portal={p}
          activeRun={activeRunsByPortal[p.slug] ?? null}
          onStartFreshRun={onStartFreshRun}
          onRetry={onRetry}
        />
      ))}
    </section>
  );
}
